import React, { useEffect, Suspense, lazy, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider, useLanguage } from "./context/LanguageContext";
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import MobileBottomNav from "./components/layout/MobileBottomNav";
import DefaultSEO from "./components/DefaultSEO";
import api from "./services/api";
import { detectLangFromPath, DEFAULT_LANG, allCategoryCitySlugs } from "./utils/seoUrls";

const CAT_CITY_SLUGS = allCategoryCitySlugs();
import { Toaster } from "sonner";

// Phase 2 SEO: Detect language from URL once at module load. If URL is /fr/...
// (non-canonical, since FR is default and has no prefix), redirect to root
// equivalent BEFORE the router mounts. Otherwise set basename to /{lang}.
const _detected = detectLangFromPath(window.location.pathname);
if (_detected.isFrPrefix) {
  const target = _detected.restPath + window.location.search + window.location.hash;
  window.location.replace(target || '/');
}
const ROUTER_BASENAME = _detected.basename;
const INITIAL_LANG = _detected.lang;

// Lazy load pages for better performance
const HomePage = lazy(() => import("./pages/HomePage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const MagicLinkPage = lazy(() => import("./pages/MagicLinkPage"));
const ListingDetailPage = lazy(() => import("./pages/ListingDetailPage"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const CreateListingPage = lazy(() => import("./pages/CreateListingPage"));
const EditListingPage = lazy(() => import("./pages/EditListingPage"));
const MessagesPage = lazy(() => import("./pages/MessagesPage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const MapSearchPage = lazy(() => import("./pages/MapSearchPage"));
const FavoritesPage = lazy(() => import("./pages/FavoritesPage"));
const SavedSearchesPage = lazy(() => import("./pages/SavedSearchesPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const UserProfilePage = lazy(() => import("./pages/UserProfilePage"));
const MyListingsPage = lazy(() => import("./pages/MyListingsPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const LaunchCountdownPage = lazy(() => import("./pages/LaunchCountdownPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const HowItWorksPage = lazy(() => import("./pages/HowItWorksPage"));
const SafetyTipsPage = lazy(() => import("./pages/SafetyTipsPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const CookiePolicyPage = lazy(() => import("./pages/CookiePolicyPage"));
const SEOLandingPage = lazy(() => import("./pages/SEOLandingPage"));
const CategoryCityPage = lazy(() => import("./pages/CategoryCityPage"));
const LocalitiesPage = lazy(() => import("./pages/LocalitiesPage"));
const KeywordLandingPage = lazy(() => import("./pages/KeywordLandingPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

// Loading spinner for lazy loaded components
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
  </div>
);

// Launch date: January 4, 2026 (Sunday) at midnight Luxembourg time (CET = UTC+1)
const LAUNCH_DATE = new Date('2026-01-04T00:00:00+01:00');
const isLaunched = () => new Date() >= LAUNCH_DATE;

// Help Button - lazy loaded
const HelpButton = lazy(() => import("./components/HelpButton"));

// Layout wrapper component with mobile bottom nav
const MainLayout = ({ children, showFooter = true, showBottomNav = true, skipSEO = false }) => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [unreadMessages, setUnreadMessages] = useState(0);
  
  // Fetch unread message count for mobile nav badge
  useEffect(() => {
    const fetchUnread = async () => {
      if (user) {
        try {
          const response = await api.get('/conversations/unread/count');
          if (response.data?.unread_count) {
            setUnreadMessages(response.data.unread_count);
          }
        } catch (e) {
          // Ignore errors
        }
      }
    };
    fetchUnread();
    const interval = user ? setInterval(fetchUnread, 60000) : null;
    return () => interval && clearInterval(interval);
  }, [user]);
  
  return (
    <>
      {!skipSEO && <DefaultSEO lang={language} />}
      <Header />
      <main className="min-h-screen pb-20 md:pb-0">{children}</main>
      {showFooter && <Footer />}
      {showBottomNav && <MobileBottomNav unreadCount={unreadMessages} />}
      <Suspense fallback={null}>
        <HelpButton />
      </Suspense>
    </>
  );
};

// Auth layout without header/footer
const AuthLayout = ({ children }) => {
  return <>{children}</>;
};

// Pre-launch route guard - only allows specific pages before launch
const PreLaunchGuard = ({ children, allowedPreLaunch = false }) => {
  const { isAuthenticated } = useAuth();
  
  // If launched, allow everything
  if (isLaunched()) {
    return children;
  }
  
  // If allowed pre-launch, show it
  if (allowedPreLaunch) {
    return children;
  }
  
  // Before launch, redirect to countdown
  return <Navigate to="/" replace />;
};

// Router wrapper to handle OAuth callback and pre-launch logic
function AppRouter() {
  const location = useLocation();
  const { isAuthenticated, loading } = useAuth();
  const launched = isLaunched();
  
  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions by processing new session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Show loading state while auth is initializing
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  // PRE-LAUNCH MODE: Show countdown page and restrict access
  if (!launched) {
    return (
      <Routes>
        {/* Countdown page as home */}
        <Route path="/" element={<LaunchCountdownPage />} />
        
        {/* Auth pages - always accessible for registration */}
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/magic" element={<MagicLinkPage />} />
        <Route path="/login" element={<AuthLayout><LoginPage /></AuthLayout>} />
        <Route path="/register" element={<AuthLayout><RegisterPage /></AuthLayout>} />
        <Route path="/forgot-password" element={<AuthLayout><ForgotPasswordPage /></AuthLayout>} />
        <Route path="/reset-password" element={<AuthLayout><ResetPasswordPage /></AuthLayout>} />
        
        {/* Static info pages - always accessible */}
        <Route path="/about" element={<MainLayout><AboutPage /></MainLayout>} />
        <Route path="/how-it-works" element={<MainLayout><HowItWorksPage /></MainLayout>} />
        <Route path="/safety" element={<MainLayout><SafetyTipsPage /></MainLayout>} />
        <Route path="/faq" element={<MainLayout><FAQPage /></MainLayout>} />
        <Route path="/contact" element={<MainLayout><ContactPage /></MainLayout>} />
        <Route path="/careers" element={<MainLayout><CareersPage /></MainLayout>} />
        <Route path="/privacy" element={<MainLayout><PrivacyPolicyPage /></MainLayout>} />
        <Route path="/terms" element={<MainLayout><TermsOfServicePage /></MainLayout>} />
        <Route path="/cookies" element={<MainLayout><CookiePolicyPage /></MainLayout>} />
        
        {/* Profile - only for logged-in users */}
        <Route path="/profile" element={
          isAuthenticated ? <MainLayout><ProfilePage /></MainLayout> : <Navigate to="/login" replace />
        } />
        <Route path="/saved-searches" element={<MainLayout skipSEO><SavedSearchesPage /></MainLayout>} />
        
        {/* Messages - only for logged-in users (needed for contact form replies) */}
        <Route path="/messages" element={
          isAuthenticated ? <MainLayout showFooter={false}><MessagesPage /></MainLayout> : <Navigate to="/login" replace />
        } />
        
        {/* Admin Dashboard - always accessible for admins */}
        <Route path="/admin" element={
          isAuthenticated ? <AuthLayout><AdminDashboard /></AuthLayout> : <Navigate to="/login" replace />
        } />
        
        {/* All other routes redirect to countdown */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }
  
  // POST-LAUNCH: Full site access
  return (
    <Routes>
      {/* Auth pages - no header/footer */}
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/magic" element={<MagicLinkPage />} />
      <Route path="/login" element={<AuthLayout><LoginPage /></AuthLayout>} />
      <Route path="/register" element={<AuthLayout><RegisterPage /></AuthLayout>} />
      <Route path="/forgot-password" element={<AuthLayout><ForgotPasswordPage /></AuthLayout>} />
      <Route path="/reset-password" element={<AuthLayout><ResetPasswordPage /></AuthLayout>} />
      
      {/* Static info pages */}
      <Route path="/about" element={<MainLayout><AboutPage /></MainLayout>} />
      <Route path="/how-it-works" element={<MainLayout><HowItWorksPage /></MainLayout>} />
      <Route path="/safety" element={<MainLayout><SafetyTipsPage /></MainLayout>} />
      <Route path="/faq" element={<MainLayout><FAQPage /></MainLayout>} />
      <Route path="/contact" element={<MainLayout><ContactPage /></MainLayout>} />
      <Route path="/careers" element={<MainLayout><CareersPage /></MainLayout>} />
      <Route path="/privacy" element={<MainLayout><PrivacyPolicyPage /></MainLayout>} />
      <Route path="/terms" element={<MainLayout><TermsOfServicePage /></MainLayout>} />
      <Route path="/cookies" element={<MainLayout><CookiePolicyPage /></MainLayout>} />
      
      {/* Main pages with header/footer */}
      <Route path="/" element={<MainLayout><HomePage /></MainLayout>} />
      <Route path="/listing/:id" element={<MainLayout skipSEO><ListingDetailPage /></MainLayout>} />
      {/* New canonical pretty URL pattern for listings.
          BrowserRouter basename adds /{lang}/ for non-default languages, so
          we only declare the noun-prefixed routes here. ListingDetailPage
          extracts the trailing seo_id from :slugTail. */}
      <Route path="/annonce/:category/:slugTail" element={<MainLayout skipSEO><ListingDetailPage /></MainLayout>} />
      <Route path="/listing/:category/:slugTail" element={<MainLayout skipSEO><ListingDetailPage /></MainLayout>} />
      <Route path="/anzeige/:category/:slugTail" element={<MainLayout skipSEO><ListingDetailPage /></MainLayout>} />
      <Route path="/category/:categoryId" element={<MainLayout skipSEO><CategoryPage /></MainLayout>} />
      <Route path="/search" element={<MainLayout showFooter={false} skipSEO><SearchPage /></MainLayout>} />
      <Route path="/map-search" element={<AuthLayout><MapSearchPage /></AuthLayout>} />
      <Route path="/favorites" element={<MainLayout><FavoritesPage /></MainLayout>} />
      <Route path="/saved-searches" element={<MainLayout skipSEO><SavedSearchesPage /></MainLayout>} />
      <Route path="/profile" element={<MainLayout><ProfilePage /></MainLayout>} />
      <Route path="/user/:id" element={<MainLayout><UserProfilePage /></MainLayout>} />
      <Route path="/my-listings" element={<MainLayout><MyListingsPage /></MainLayout>} />
      <Route path="/messages" element={<MainLayout showFooter={false}><MessagesPage /></MainLayout>} />
      
      {/* Create/Edit listing - special layout */}
      <Route path="/create-listing" element={<AuthLayout><CreateListingPage /></AuthLayout>} />
      <Route path="/edit-listing/:id" element={<AuthLayout><EditListingPage /></AuthLayout>} />
      
      {/* Admin Dashboard */}
      <Route path="/admin" element={<AuthLayout><AdminDashboard /></AuthLayout>} />
      
      {/* SEO Landing Pages - Programmatic SEO */}
      <Route path="/annonces/:citySlug" element={<MainLayout skipSEO><SEOLandingPage /></MainLayout>} />
      <Route path="/garages/:citySlug" element={<MainLayout skipSEO><SEOLandingPage /></MainLayout>} />
      {/* Programmatic local SEO pages: category × locality (all localized slugs) */}
      {CAT_CITY_SLUGS.map((slug) => (
        <Route key={slug} path={`/${slug}/:citySlug`} element={<MainLayout skipSEO><CategoryCityPage /></MainLayout>} />
      ))}
      <Route path="/villes" element={<MainLayout skipSEO><LocalitiesPage /></MainLayout>} />
      <Route path="/cities" element={<MainLayout skipSEO><LocalitiesPage /></MainLayout>} />
      <Route path="/staedte" element={<MainLayout skipSEO><LocalitiesPage /></MainLayout>} />
      <Route path="/stied" element={<MainLayout skipSEO><LocalitiesPage /></MainLayout>} />
      
      {/* Keyword-focused Landing Pages for high-value search terms */}
      <Route path="/seo/:keyword" element={<MainLayout skipSEO><KeywordLandingPage /></MainLayout>} />

      {/* 404 fallback - must be last */}
      <Route path="*" element={<MainLayout><NotFoundPage /></MainLayout>} />
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider initialLang={INITIAL_LANG}>
      <AuthProvider>
        <div className="App">
          <BrowserRouter basename={ROUTER_BASENAME}>
            <Suspense fallback={<PageLoader />}>
              <AppRouter />
            </Suspense>
            <Toaster position="top-center" richColors />
          </BrowserRouter>
        </div>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
